import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Facebook, 
  Instagram, 
  X, 
  MessageCircle,
  Linkedin,
  Youtube
} from 'lucide-react';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';

const ConfigurableFooter = () => {
  const [footerConfig, setFooterConfig] = useState(null);
  const [hospitalHours, setHospitalHours] = useState(null);
  const [urgentCareHours, setUrgentCareHours] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [businessModelSettings, setBusinessModelSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { urgent_care_enabled } = useSettings();
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDayHours = (dayData) => {
    if (!dayData || !dayData.is_open) {
      return 'Closed';
    }
    return `${formatTime(dayData.open_time)} - ${formatTime(dayData.close_time)}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch footer configuration from public endpoint (with cache-busting)
        const footerResponse = await axios.get(`${API_BASE_URL}/api/footer-config`, {
          headers: { 'Cache-Control': 'no-cache' },
          params: { _t: Date.now() }
        });
        console.log('Footer config fetched:', footerResponse.data?.config);
        setFooterConfig(footerResponse.data?.config || {});

        // Fetch hospital hours
        const hospitalResponse = await fetch(`${API_BASE_URL}/api/hospital-hours`);
        if (hospitalResponse.ok) {
          const hospitalData = await hospitalResponse.json();
          setHospitalHours(hospitalData);
        }

        // Fetch urgent care hours
        const urgentResponse = await fetch(`${API_BASE_URL}/api/urgent-care-hours`);
        if (urgentResponse.ok) {
          const urgentData = await urgentResponse.json();
          setUrgentCareHours(urgentData);
        }

        // Fetch business info (includes additional_locations)
        const businessResponse = await fetch(`${API_BASE_URL}/api/business-info`);
        if (businessResponse.ok) {
          const businessData = await businessResponse.json();
          setBusinessInfo(businessData);
        }

        // Fetch business model settings
        const modelSettingsResponse = await axios.get(`${API_BASE_URL}/api/business-model-settings`);
        if (modelSettingsResponse.data) {
          setBusinessModelSettings(modelSettingsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching footer data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // Get social media icon component
  const getSocialIcon = (platform) => {
    const icons = {
      'facebook': Facebook,
      'instagram': Instagram,
      'twitter': X,
      'whatsapp': MessageCircle,
      'linkedin': Linkedin,
      'youtube': Youtube
    };
    return icons[platform] || Facebook;
  };

  // Default config if not loaded
  const config = footerConfig || {};
  const showContactInfo = config.show_contact_info !== false;
  const showAddress = config.show_address !== false;
  const showPhone = config.show_phone !== false;
  const showEmail = config.show_email !== false;
  const showSocialMedia = config.show_social_media !== false;
  
  // Don't show hours if business is online only
  const isOnlineOnly = businessModelSettings?.online_only || false;
  const showHours = !isOnlineOnly && config.show_hours !== false;
  
  const showQuickLinks = config.show_quick_links !== false;
  const showLegalLinks = config.show_legal_links !== false;
  const copyrightText = config.copyright_text || `© ${new Date().getFullYear()} All Rights Reserved`;
  
  // Which business hours to show
  const showFirstBusinessHours = config.show_first_business_hours !== false;
  const showSecondBusinessHours = config.show_second_business_hours !== false;
  
  // Layout configuration
  const layoutPrimaryAddress = config.layout_primary_address || 'left';
  const layoutAdditionalLocations = config.layout_additional_locations || 'below-primary';
  const layoutQuickLinks = config.layout_quick_links || 'right';
  const quickLinksColumns = config.quick_links_columns || '1';
  const quickLinksSpan = config.layout_quick_links_span || 'none';
  const layoutSocialMedia = config.layout_social_media || 'below-primary';
  const socialMediaStyle = config.social_media_style || 'icons';
  const layoutBusinessHours = config.layout_business_hours || 'center-left';
  const showAdditionalLocations = config.show_additional_locations !== false;
  
  // Custom labels from footer config (with fallbacks)
  const labelQuickLinks = config.label_quick_links || 'Quick Links';
  const labelFollowUs = config.label_follow_us || 'Follow Us';
  const labelLocations = config.label_locations || 'Our Locations';
  const labelFirstHours = config.label_first_hours || businessModelSettings?.business_hours_label || 'Store Hours';
  const labelSecondHours = config.label_second_hours || businessModelSettings?.second_business_hours_label || 'Urgent Care Hours';
  
  // Debug logging for layout configuration
  console.log('Footer Layout Config:', {
    layoutPrimaryAddress,
    layoutAdditionalLocations,
    layoutQuickLinks,
    quickLinksColumns,
    layoutSocialMedia,
    socialMediaStyle,
    layoutBusinessHours,
    showAdditionalLocations,
    labels: { labelQuickLinks, labelFollowUs, labelLocations, labelFirstHours, labelSecondHours }
  });
  
  // Get additional locations from business info
  const additionalLocations = (businessInfo?.multiple_locations_enabled && businessInfo?.additional_locations) || [];
  
  const quickLinks = config.quick_links || [
    { label: 'About Us', url: '/about' },
    { label: 'Services', url: '/services' },
    { label: 'Contact', url: '/reach-us' }
  ];

  const legalLinks = config.legal_links || [
    { label: 'Terms of Service', url: '/terms' },
    { label: 'Privacy Policy', url: '/privacy' },
    { label: 'Accessibility', url: '/accessibility' }
  ];

  // Social media links - use business info if enabled, otherwise use config
  const getSocialMediaLinks = () => {
    if (config.use_business_info_social !== false && businessInfo) {
      const links = [];
      if (businessInfo.facebook_link && businessInfo.facebook_link.trim()) {
        links.push({ platform: 'facebook', url: businessInfo.facebook_link });
      }
      if (businessInfo.instagram_link && businessInfo.instagram_link.trim()) {
        links.push({ platform: 'instagram', url: businessInfo.instagram_link });
      }
      if (businessInfo.twitter_link && businessInfo.twitter_link.trim()) {
        links.push({ platform: 'twitter', url: businessInfo.twitter_link });
      }
      if (businessInfo.whatsapp_link && businessInfo.whatsapp_link.trim()) {
        links.push({ platform: 'whatsapp', url: businessInfo.whatsapp_link });
      }
      if (businessInfo.linkedin_link && businessInfo.linkedin_link.trim()) {
        links.push({ platform: 'linkedin', url: businessInfo.linkedin_link });
      }
      if (businessInfo.youtube_link && businessInfo.youtube_link.trim()) {
        links.push({ platform: 'youtube', url: businessInfo.youtube_link });
      }
      return links;
    }
    return config.social_media || [];
  };

  const socialMediaLinks = getSocialMediaLinks();

  // Get business hours labels (fallback to custom labels from footer config)
  const firstBusinessLabel = labelFirstHours;
  const secondBusinessLabel = labelSecondHours;
  const multipleBusinessesEnabled = businessModelSettings?.multiple_businesses_enabled || false;

  // Helper to render Primary Address section
  const renderPrimaryAddress = () => (
    <div className="space-y-4">
      {/* Logo and Title */}
      <div className="flex items-center space-x-3">
        <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--theme-primary)' }}>
          <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
            <div className="relative">
              <div 
                className="absolute" 
                style={{
                  width: '16px',
                  height: '4px',
                  backgroundColor: 'var(--theme-primary)',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              ></div>
              <div 
                className="absolute" 
                style={{
                  width: '4px',
                  height: '16px',
                  backgroundColor: 'var(--theme-primary)',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              ></div>
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-lg font-bold">{businessInfo?.website_title || businessInfo?.hospital_name || 'Pets & Vets'}</h3>
          <p className="text-sm text-gray-400">{businessInfo?.website_subtitle || 'Animal Hospital'}</p>
        </div>
      </div>
      
      {/* Contact Details */}
      <div className="space-y-3">
        {showAddress && businessInfo?.address && (
          <div className="flex items-start space-x-3">
            <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--theme-secondary)' }} />
            <p className="text-gray-300 text-sm">{businessInfo.address}</p>
          </div>
        )}
        {showPhone && businessInfo?.phone && (
          <div className="flex items-center space-x-3">
            <Phone className="h-5 w-5" style={{ color: 'var(--theme-secondary)' }} />
            <a 
              href={`tel:${businessInfo.phone}`} 
              className="text-gray-300 hover:text-white transition-colors text-sm"
            >
              {businessInfo.phone}
            </a>
          </div>
        )}
        {showEmail && businessInfo?.email && (
          <div className="flex items-center space-x-3">
            <Mail className="h-5 w-5" style={{ color: 'var(--theme-secondary)' }} />
            <a 
              href={`mailto:${businessInfo.email}`} 
              className="text-gray-300 hover:text-white transition-colors text-sm"
            >
              {businessInfo.email}
            </a>
          </div>
        )}
      </div>

      {/* Social Media Icons - if set to below primary */}
      {layoutSocialMedia === 'below-primary' && showSocialMedia && socialMediaLinks.length > 0 && (
        socialMediaStyle === 'icons' ? (
          <div className="flex space-x-4 pt-2">
            {socialMediaLinks.map((social, index) => {
              const IconComponent = getSocialIcon(social.platform);
              return (
                <a 
                  key={index}
                  href={social.url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  title={social.platform}
                >
                  <IconComponent className="h-5 w-5" />
                </a>
              );
            })}
          </div>
        ) : (
          <div className="pt-2">
            <h5 className="text-sm font-semibold text-white mb-2">{labelFollowUs}</h5>
            <ul className="space-y-2">
              {socialMediaLinks.map((social, index) => {
                const IconComponent = getSocialIcon(social.platform);
                return (
                  <li key={index}>
                    <a 
                      href={social.url} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                    >
                      <IconComponent className="h-4 w-4" style={{ color: 'var(--theme-secondary)' }} />
                      <span className="text-sm capitalize">{social.platform}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )
      )}

      {/* Additional Locations - if set to below primary */}
      {layoutAdditionalLocations === 'below-primary' && showAdditionalLocations && additionalLocations.length > 0 && (
        <div className="pt-4 border-t border-gray-700">
          <h5 className="text-sm font-semibold text-white mb-3">Other Locations</h5>
          <div className="space-y-3">
            {additionalLocations.map((location, index) => (
              <div key={location.id || index} className="space-y-1">
                <p className="text-sm font-medium text-white">{location.name}</p>
                {location.address && (
                  <p className="text-xs text-gray-400">{location.address}</p>
                )}
                {location.phone && (
                  <a href={`tel:${location.phone}`} className="text-xs text-gray-300 hover:text-white block">
                    {location.phone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Helper to render Additional Locations as separate column
  const renderAdditionalLocations = () => {
    if (!showAdditionalLocations || additionalLocations.length === 0 || layoutAdditionalLocations === 'below-primary') {
      return null;
    }
    return (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white">{labelLocations}</h4>
        <div className="space-y-4">
          {additionalLocations.map((location, index) => (
            <div key={location.id || index} className="space-y-2">
              <h5 className="text-sm font-medium text-white">{location.name}</h5>
              {location.address && (
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" />
                  <p className="text-gray-300 text-xs">{location.address}</p>
                </div>
              )}
              {location.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <a 
                    href={`tel:${location.phone}`} 
                    className="text-gray-300 hover:text-white transition-colors text-xs"
                  >
                    {location.phone}
                  </a>
                </div>
              )}
              {location.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <a 
                    href={`mailto:${location.email}`} 
                    className="text-gray-300 hover:text-white transition-colors text-xs"
                  >
                    {location.email}
                  </a>
                </div>
              )}
              {index < additionalLocations.length - 1 && (
                <div className="border-b border-gray-700 pt-2"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper to render Social Media as separate column
  const renderSocialMedia = () => {
    if (!showSocialMedia || socialMediaLinks.length === 0 || layoutSocialMedia === 'below-primary') {
      return null;
    }
    return (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white">{labelFollowUs}</h4>
        {socialMediaStyle === 'icons' ? (
          <div className="flex flex-wrap gap-4">
            {socialMediaLinks.map((social, index) => {
              const IconComponent = getSocialIcon(social.platform);
              return (
                <a 
                  key={index}
                  href={social.url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  title={social.platform}
                >
                  <IconComponent className="h-6 w-6" />
                </a>
              );
            })}
          </div>
        ) : (
          <ul className="space-y-3">
            {socialMediaLinks.map((social, index) => {
              const IconComponent = getSocialIcon(social.platform);
              return (
                <li key={index}>
                  <a 
                    href={social.url} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
                  >
                    <IconComponent className="h-5 w-5 group-hover:scale-110 transition-transform" style={{ color: 'var(--theme-secondary)' }} />
                    <span className="text-sm capitalize">{social.platform}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  // Helper to render Business Hours
  const renderBusinessHours = () => {
    if (isOnlineOnly || !showHours || (!showFirstBusinessHours && !showSecondBusinessHours)) {
      return null;
    }
    return (
      <div className="space-y-4">
        {/* First Business Hours */}
        {showFirstBusinessHours && hospitalHours && (
          <div>
            <h4 className="text-lg font-semibold text-white flex items-center mb-3">
              <Clock className="h-5 w-5 mr-2" style={{ color: 'var(--theme-secondary)' }} />
              {firstBusinessLabel}
            </h4>
            <div className="space-y-1 text-sm">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <div key={day} className="flex justify-between">
                  <span className="text-gray-300 capitalize">{day}:</span>
                  <span className="text-white">{formatDayHours(hospitalHours[day])}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Second Business Hours */}
        {showSecondBusinessHours && multipleBusinessesEnabled && urgentCareHours && (
          <div className={showFirstBusinessHours ? 'pt-4 border-t border-gray-700' : ''}>
            <h4 className="text-lg font-semibold text-white flex items-center mb-3">
              <Clock className="h-5 w-5 mr-2" style={{ color: 'var(--theme-secondary)' }} />
              {secondBusinessLabel}
            </h4>
            <div className="space-y-1 text-sm">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <div key={day} className="flex justify-between">
                  <span className="text-gray-300 capitalize">{day}:</span>
                  <span className="text-white">{formatDayHours(urgentCareHours[day])}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper to render Quick Links
  const renderQuickLinks = () => {
    if (!showQuickLinks || quickLinks.length === 0) {
      return null;
    }
    return (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white">{labelQuickLinks}</h4>
        <ul className={quickLinksColumns === '2' ? 'grid grid-cols-2 gap-x-4 gap-y-2' : 'space-y-2'}>
          {quickLinks.map((link, index) => (
            <li key={index}>
              <Link 
                to={link.url} 
                className="text-gray-300 hover:text-white transition-colors text-sm"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Organize content by columns
  const getColumnContent = (column) => {
    const content = [];
    
    // Primary Address
    if (layoutPrimaryAddress === column && showContactInfo) {
      content.push({ key: 'primary', render: renderPrimaryAddress });
    }
    
    // Additional Locations (if separate column)
    if (layoutAdditionalLocations === column && showAdditionalLocations && additionalLocations.length > 0) {
      content.push({ key: 'locations', render: renderAdditionalLocations });
    }
    
    // Social Media (if separate column)
    if (layoutSocialMedia === column && showSocialMedia && socialMediaLinks.length > 0) {
      content.push({ key: 'social', render: renderSocialMedia });
    }
    
    // Business Hours
    if (layoutBusinessHours === column && showHours && !isOnlineOnly) {
      content.push({ key: 'hours', render: renderBusinessHours });
    }
    
    // Quick Links
    if (layoutQuickLinks === column && showQuickLinks) {
      content.push({ key: 'quicklinks', render: renderQuickLinks });
    }
    
    return content;
  };

  // Get all columns that have content
  const columns = ['left', 'center-left', 'center-right', 'right'];
  const activeColumns = columns.filter(col => getColumnContent(col).length > 0);
  
  // Debug: Log active columns and what's in each
  console.log('Footer Active Columns:', activeColumns);
  columns.forEach(col => {
    const content = getColumnContent(col);
    console.log(`Column ${col}:`, content.map(c => c.key));
  });
  
  // Calculate grid columns based on active content
  const gridCols = Math.max(1, Math.min(4, activeColumns.length));
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  }[gridCols];
  
  console.log('Footer grid:', gridCols, 'cols, class:', gridClass);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Footer Content */}
        <div className={`grid ${gridClass} gap-6`}>
          {activeColumns.map(column => {
            const columnContent = getColumnContent(column);
            if (columnContent.length === 0) return null;
            
            return (
              <div key={column} className="space-y-6">
                {columnContent.map(item => (
                  <React.Fragment key={item.key}>
                    {item.render()}
                  </React.Fragment>
                ))}
              </div>
            );
          })}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-6 pt-6 text-center">
          {/* Legal Links */}
          {showLegalLinks && legalLinks.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 mb-4">
              {legalLinks.map((link, index) => (
                <React.Fragment key={index}>
                  <Link 
                    to={link.url} 
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                  {index < legalLinks.length - 1 && (
                    <span className="hidden sm:inline text-gray-600">•</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          
          {/* Copyright */}
          <p className="text-gray-400 text-sm">
            {copyrightText}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default ConfigurableFooter;
